import { useEffect, useMemo, useState } from 'react';
import { useInclusionRequestStore, type DecisionDeLEquipe, type InclusionRequest } from '../../stores/inclusionRequestStore';
import { Loader2, AlertCircle, Archive, Filter, Search, FileText, CheckCircle, X, Clock, RefreshCw, Mail, UserPlus } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import toast, { Toaster } from 'react-hot-toast';
import clsx from 'clsx';
import InscriptionDepuisDemandeModal from '../../components/admin/InscriptionDepuisDemandeModal';
import Telephone from '../../components/admin/Telephone';
import { demandesDe, regrouperATraiter, semainesAInscrire, semainesNonInscrites, un, type GroupeATraiter } from '../../lib/inclusion/regroupement';
import { COULEUR_DU_NIVEAU, DESCRIPTION_DES_NIVEAUX, libelleDuNiveau } from '../../lib/inclusion/niveaux';

/*
  Les demandes d'inclusion (refait le 26/10/2026, décisions d'Éclosion).

  Le parent coche ses semaines (inscriptions ouvertes) et envoie une demande ;
  l'équipe la reçoit par courriel (info@, Samy en copie) et ici, regroupée par
  enfant. L'examiner, c'est aussi placer l'enfant à un niveau d'accès :
  - « Accepter et inscrire » : l'administration confirme l'inscription aux
    semaines retenues — même circuit que l'inscription admin (`create_order`,
    facture envoyée) —, place l'enfant au niveau 1 (ses parents l'inscrivent
    désormais comme tout enfant) ou 2 (chaque inscription par une demande), et
    le parent reçoit la réponse ;
  - « Refuser » : pour l'une des deux raisons (accueil en sécurité impossible,
    ou un autre enfant déjà accueilli en inclusion et plus de place), au
    niveau 2 (d'autres demandes restent possibles) ou 3 (accueil impossible) ;
    le parent reçoit la réponse ;
  - « Classer » : ni acceptée ni refusée (déjà inscrit autrement, semaine plus
    d'actualité) ; personne n'est prévenu.

  L'ancien « Approuver » insérait lui-même la ligne d'inscription, au prix
  normal, sans facture — et la RLS le refuse depuis août 2026 : la demande
  passait « approuvée » sans que l'enfant soit inscrit.
*/

type Filtre = 'a-traiter' | 'all' | 'pending' | 'converted' | 'rejected' | 'archived' | 'approved';
type Raison = 'securite' | 'place';

const RAISONS: { valeur: Raison; libelle: string }[] = [
  { valeur: 'securite', libelle: 'Nous ne pouvons pas assurer un accueil en sécurité' },
  { valeur: 'place', libelle: 'Un autre enfant est déjà accueilli en inclusion : plus de place pour le moment' },
];

const aujourdhui = () => new Date().toISOString().slice(0, 10);
const date = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR');

const decisionDe = (request: InclusionRequest): DecisionDeLEquipe | null => un(un(request.kid)?.inclusion);

/** La réponse au parent peut (encore) partir : décidée, sur une semaine pas finie depuis plus d'une semaine. */
const aPrevenir = (r: InclusionRequest) => {
  const fin = un(r.session)?.end_date ?? '';
  const limite = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  return !r.reponse_envoyee_le && fin >= limite
    && (r.status === 'converted' || (r.status === 'rejected' && !!r.raison_refus));
};

const correspond = (texte: string, ...champs: (string | null | undefined)[]) =>
  !texte || champs.some((c) => c?.toLowerCase().includes(texte.toLowerCase()));

const AdminInclusionRequestsPage = () => {
  const {
    requests, inscriptionsVivantes, isLoading, error, fetchRequests, updateRequestStatus, deciderLesDemandes, prevenirLeParent,
    poserNiveau,
  } = useInclusionRequestStore();

  const [filter, setFilter] = useState<Filtre>('a-traiter');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);

  const [aAccepter, setAAccepter] = useState<GroupeATraiter | null>(null);
  const [aRefuser, setARefuser] = useState<GroupeATraiter | null>(null);
  const [aClasser, setAClasser] = useState<GroupeATraiter | null>(null);
  const [semainesRefusees, setSemainesRefusees] = useState<string[]>([]);
  const [raison, setRaison] = useState<Raison | null>(null);
  const [niveauRefus, setNiveauRefus] = useState<2 | 3 | null>(null);
  const [mot, setMot] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const groupes = useMemo(
    () => regrouperATraiter(requests, new Set(inscriptionsVivantes), aujourdhui())
      .filter((g) => correspond(searchTerm, g.enfant.prenom, g.enfant.nom, g.parent.prenom, g.parent.nom, g.parent.email)),
    [requests, inscriptionsVivantes, searchTerm],
  );

  const handleViewDetails = (requestId: string) => {
    const request = requests.find(r => r.id === requestId);
    if (request) {
      setSelectedRequest(requestId);
      setAdminNotes(request.admin_notes || '');
      setIsDetailsModalOpen(true);
    }
  };

  const ouvrirRefus = (g: GroupeATraiter) => {
    setARefuser(g);
    setSemainesRefusees(semainesNonInscrites(g).map((s) => s.sessionId));
    setRaison(null);
    setNiveauRefus(g.niveau === 2 || g.niveau === 3 ? g.niveau : null);
    setMot('');
  };

  const refuser = async () => {
    if (!aRefuser || !raison || !niveauRefus) return;
    const semaines = aRefuser.semaines.filter((s) => semainesRefusees.includes(s.sessionId));
    const ids = demandesDe(semaines);
    if (ids.length === 0) return;
    setEnCours(true);
    let refusees: string[];
    try {
      refusees = await deciderLesDemandes(ids, 'rejected', { raison_refus: raison, mot_au_parent: mot });
    } catch (err) {
      console.error('Error rejecting inclusion requests:', err);
      toast.error(`Le refus n'a pas pu être enregistré${err instanceof Error && err.message ? ` : ${err.message}` : '.'}`);
      setEnCours(false);
      return;
    }
    /* Le niveau avant le courriel : celui-ci dit au parent si d'autres demandes restent possibles. */
    if (niveauRefus !== aRefuser.niveau) {
      try {
        await poserNiveau(aRefuser.kidId, niveauRefus, `Demande d'inclusion refusée (${raison === 'place' ? 'plus de place' : 'sécurité'})`);
      } catch {
        toast.error(`Refus enregistré, mais le niveau ${niveauRefus} n'a pas pu l'être : posez-le depuis la fiche de l'enfant.`, { duration: 8000 });
      }
    }
    try {
      await prevenirLeParent(refusees);
      toast.success(`Demande refusée : ${aRefuser.parent.prenom || 'le parent'} est prévenu·e par courriel.`);
    } catch {
      toast.error('Refus enregistré, mais le courriel au parent n\'est pas parti : « Prévenir le parent » dans les demandes refusées.', { duration: 8000 });
    }
    setEnCours(false);
    setARefuser(null);
  };

  const classer = async () => {
    if (!aClasser) return;
    setEnCours(true);
    try {
      await deciderLesDemandes(demandesDe(aClasser.semaines), 'archived');
      toast.success(`Demandes de ${aClasser.enfant.prenom} classées.`);
      setAClasser(null);
    } catch (err) {
      console.error('Error archiving inclusion requests:', err);
      toast.error(`Le classement n'a pas pu être enregistré${err instanceof Error && err.message ? ` : ${err.message}` : '.'}`);
    } finally {
      setEnCours(false);
    }
  };

  /* Les lignes d'une même réponse : même enfant, même décision, pas encore prévenu. */
  const prevenir = async (request: InclusionRequest) => {
    const ids = requests
      .filter((r) => r.kid_id === request.kid_id && r.status === request.status && aPrevenir(r))
      .map((r) => r.id);
    setProcessingRequestId(request.id);
    try {
      const r = await prevenirLeParent(ids);
      toast.success(r.deja ? 'Le parent avait déjà été prévenu.' : 'Le parent est prévenu par courriel.');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Le courriel n\'est pas parti.');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedRequest) return;

    try {
      setProcessingRequestId(selectedRequest);
      await updateRequestStatus(
        selectedRequest,
        requests.find(r => r.id === selectedRequest)?.status ?? 'pending',
        adminNotes
      );
      setIsDetailsModalOpen(false);
      toast.success('Notes enregistrées');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Une erreur est survenue lors de l\'enregistrement des notes');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const filteredRequests = requests
    .filter(req => filter === 'all' || req.status === filter)
    .filter(req => {
      const kid = un(req.kid);
      const parent = un(req.parent);
      const stage = un(un(req.session)?.stage);
      return correspond(searchTerm, kid?.prenom, kid?.nom, parent?.prenom, parent?.nom, parent?.email, stage?.title);
    });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            En attente
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approuvée (ancien circuit)
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <X className="h-3 w-3 mr-1" />
            Refusée
          </span>
        );
      case 'converted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Acceptée, inscrit·e
          </span>
        );
      case 'archived':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <Archive className="h-3 w-3 mr-1" />
            Classée
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Demandes d'inclusion</h1>
          <p className="text-gray-600">
            Le parent coche ses semaines et envoie une demande, avec la fiche de l'enfant (courriel à info@, Samy en copie).
            Accepter inscrit l'enfant et envoie la facture ; refuser prévient le parent ; classer ne prévient personne.
          </p>
        </div>

        <button
          onClick={() => fetchRequests()}
          className="btn-primary flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Rechercher..."
            className="form-input pl-10 pr-4 py-2 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
        </div>

        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-500" />
          <select
            className="form-input py-2"
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filtre)}
          >
            <option value="a-traiter">À traiter (semaines à venir)</option>
            <option value="all">Toutes</option>
            <option value="pending">En attente</option>
            <option value="converted">Acceptées, inscrits</option>
            <option value="rejected">Refusées</option>
            <option value="archived">Classées</option>
            <option value="approved">Approuvées (ancien circuit)</option>
          </select>
        </div>
      </div>

      {isLoading && requests.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      ) : filter === 'a-traiter' ? (
        groupes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center text-gray-600">
            {searchTerm ? 'Aucune demande ne correspond à vos critères.' : 'Rien à traiter : aucune demande en attente sur une semaine à venir.'}
          </div>
        ) : (
          <div className="space-y-4">
            {groupes.map((g) => {
              const aInscrire = semainesAInscrire(g);
              return (
                <section key={`${g.kidId}|${g.parentId}`} className="rounded-xl bg-white p-4 shadow-md sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {g.enfant.prenom} {g.enfant.nom}
                        <span className={clsx('ml-2 inline-block rounded-full px-2 py-0.5 align-middle text-xs font-normal', COULEUR_DU_NIVEAU[g.niveau ?? 'aucun'])}>
                          {libelleDuNiveau(g.niveau)}
                        </span>
                      </h2>
                      <p className="text-sm text-gray-600">
                        {g.parent.prenom} {g.parent.nom} · {g.parent.email ?? 'Aucune adresse'} · <Telephone numero={g.parent.telephone} />
                      </p>
                      <p className="text-xs text-gray-500">Demandé le {date(g.premiereDemande)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setAAccepter(g)}
                        disabled={aInscrire.length === 0}
                        className="btn-primary flex items-center gap-1 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                        title={aInscrire.length === 0 ? 'Aucune semaine à inscrire : déjà inscrites, ou inscriptions pas encore ouvertes.' : undefined}
                      >
                        <UserPlus className="h-4 w-4" /> Accepter et inscrire…
                      </button>
                      <button type="button" onClick={() => ouvrirRefus(g)} className="btn-outline flex items-center gap-1 px-3 py-1.5 text-sm text-red-700">
                        <X className="h-4 w-4" /> Refuser…
                      </button>
                      <button type="button" onClick={() => setAClasser(g)} className="btn-outline flex items-center gap-1 px-3 py-1.5 text-sm">
                        <Archive className="h-4 w-4" /> Classer
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewDetails(g.semaines[0].demandeIds[0])}
                        className="flex items-center gap-1 px-2 py-1.5 text-sm text-primary-700 hover:underline"
                      >
                        <FileText className="h-4 w-4" /> Fiche
                      </button>
                    </div>
                  </div>
                  <ul className="mt-3 divide-y rounded-lg border text-sm">
                    {g.semaines.map((s) => (
                      <li key={s.sessionId} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-3 py-2">
                        <span>
                          <span className="font-medium text-gray-900">Du {date(s.debut)} au {date(s.fin)}</span>
                          <span className="text-gray-600"> — {s.stage}, {s.centre}{s.periode ? ` (${s.periode})` : ''}</span>
                        </span>
                        <span className="flex gap-2 text-xs">
                          {s.dejaInscrit && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-700">Déjà inscrit·e</span>}
                          {s.demandeIds.length > 1 && (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">Envoyée {s.demandeIds.length} fois</span>
                          )}
                          {s.capacite !== null && (
                            <span className="text-gray-500">{s.inscrits ?? 0}/{s.capacite} inscrits</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <p className="text-gray-600 mb-4">
            {searchTerm || filter !== 'all'
              ? 'Aucune demande ne correspond à vos critères.'
              : 'Aucune demande d\'inclusion trouvée.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {filter === 'pending' && (
            <p className="border-b bg-gray-50 px-6 py-3 text-sm text-gray-600">
              Les demandes en attente se traitent dans « À traiter », regroupées par enfant.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enfant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activité</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => {
                  const kid = un(request.kid);
                  const parent = un(request.parent);
                  const session = un(request.session);
                  const stage = un(session?.stage);
                  const center = un(session?.center);

                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.request_date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{kid?.prenom} {kid?.nom}</div>
                        {decisionDe(request)?.niveau_acces && (
                          <span className={clsx('mt-1 inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs', COULEUR_DU_NIVEAU[decisionDe(request)?.niveau_acces ?? 'aucun'])}>
                            Niveau {decisionDe(request)?.niveau_acces}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{parent?.prenom} {parent?.nom}</div>
                        <div className="text-sm text-gray-500">{parent?.email}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{stage?.title}</div>
                        <div className="text-sm text-gray-500">{center?.name}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {session?.periode && (
                          <div className="text-xs font-medium uppercase text-gray-500">{session.periode}</div>
                        )}
                        <div className="text-sm text-gray-900">{session?.start_date && date(session.start_date)}</div>
                        <div className="text-sm text-gray-500">au {session?.end_date && date(session.end_date)}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                        {request.status === 'rejected' && request.raison_refus && (
                          <div className="mt-1 text-xs text-gray-500">
                            {request.raison_refus === 'place' ? 'Plus de place en inclusion' : 'Accueil en sécurité impossible'}
                          </div>
                        )}
                        {request.reponse_envoyee_le && (
                          <div className="mt-1 text-xs text-gray-500">Parent prévenu le {date(request.reponse_envoyee_le)}</div>
                        )}
                        {aPrevenir(request) && (
                          <button
                            type="button"
                            onClick={() => prevenir(request)}
                            disabled={processingRequestId === request.id}
                            className="mt-1 flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-900"
                            title="Le courriel au parent n'est pas parti"
                          >
                            {processingRequestId === request.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                            Prévenir le parent
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(request.id)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Voir les détails"
                        >
                          <FileText className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aAccepter && (
        <InscriptionDepuisDemandeModal
          groupe={aAccepter}
          onClose={() => setAAccepter(null)}
          onInscrit={() => fetchRequests()}
        />
      )}

      {/* Refuser */}
      <Dialog open={!!aRefuser} onClose={() => !enCours && setARefuser(null)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          {aRefuser && (
            <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="mb-4 text-lg font-semibold">Refuser la demande pour {aRefuser.enfant.prenom}</Dialog.Title>
              <fieldset className="mb-4">
                <legend className="mb-2 text-sm font-medium text-gray-900">Les semaines refusées</legend>
                <ul className="space-y-1 text-sm">
                  {aRefuser.semaines.map((s) => (
                    <li key={s.sessionId}>
                      <label className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={semainesRefusees.includes(s.sessionId)}
                          onChange={() => setSemainesRefusees((c) => (c.includes(s.sessionId) ? c.filter((x) => x !== s.sessionId) : [...c, s.sessionId]))}
                        />
                        <span>Du {date(s.debut)} au {date(s.fin)} — {s.stage}{s.dejaInscrit ? ' (déjà inscrit·e)' : ''}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </fieldset>
              <fieldset className="mb-4">
                <legend className="mb-2 text-sm font-medium text-gray-900">La raison, dite au parent</legend>
                {RAISONS.map((r) => (
                  <label key={r.valeur} className="mb-1 flex items-start gap-2 text-sm">
                    <input type="radio" name="raison" className="mt-1" checked={raison === r.valeur} onChange={() => setRaison(r.valeur)} />
                    <span>{r.libelle}</span>
                  </label>
                ))}
              </fieldset>
              <fieldset className="mb-4">
                <legend className="mb-2 text-sm font-medium text-gray-900">Le niveau d'accès de {aRefuser.enfant.prenom}</legend>
                {([2, 3] as const).map((n) => (
                  <label key={n} className="mb-1 flex items-start gap-2 text-sm">
                    <input type="radio" name="niveau-refus" className="mt-1" checked={niveauRefus === n} onChange={() => setNiveauRefus(n)} />
                    <span>
                      <span className="font-medium">Niveau {n} — {DESCRIPTION_DES_NIVEAUX[n].titre}</span>
                      <span className="block text-xs text-gray-500">
                        {n === 2 ? 'Ses parents pourront faire d\'autres demandes.' : 'Ses parents ne pourront plus ni inscrire ni demander.'}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <label className="mb-4 block text-sm">
                <span className="mb-1 block font-medium text-gray-900">Un mot pour le parent (facultatif)</span>
                <textarea
                  value={mot}
                  onChange={(e) => setMot(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Repris dans le courriel."
                />
              </label>
              <p className="mb-4 text-xs text-gray-500">
                Le parent reçoit un courriel avec la raison et votre mot ; il répond à info@eclosion.be. Sur la page des
                stages, ces semaines disent « Demande non retenue ».
              </p>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setARefuser(null)} disabled={enCours} className="btn-outline">Annuler</button>
                <button
                  type="button"
                  onClick={refuser}
                  disabled={enCours || !raison || !niveauRefus || semainesRefusees.length === 0}
                  className="btn-primary bg-red-600 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refuser et prévenir le parent'}
                </button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Classer */}
      <Dialog open={!!aClasser} onClose={() => !enCours && setAClasser(null)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          {aClasser && (
            <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="mb-3 text-lg font-semibold">Classer la demande pour {aClasser.enfant.prenom}</Dialog.Title>
              <p className="mb-4 text-sm text-gray-600">
                Ni acceptée ni refusée : elle sort de « À traiter », et personne n'est prévenu. Pour une demande déjà réglée
                autrement (enfant inscrit par l'inscription admin, semaine plus d'actualité).
              </p>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setAClasser(null)} disabled={enCours} className="btn-outline">Annuler</button>
                <button type="button" onClick={classer} disabled={enCours} className="btn-primary">
                  {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : `Classer (${aClasser.semaines.length} semaine${aClasser.semaines.length > 1 ? 's' : ''})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Details Modal */}
      <Dialog
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto p-6 max-h-[90vh] overflow-y-auto">
            <Dialog.Title className="text-lg font-semibold mb-4">
              Détails de la demande d'inclusion
            </Dialog.Title>

            {selectedRequest && (
              <div className="space-y-6">
                {(() => {
                  const request = requests.find(r => r.id === selectedRequest);
                  if (!request) return null;

                  const kid = un(request.kid);
                  const parent = un(request.parent);
                  const session = un(request.session);
                  const stage = un(session?.stage);
                  const decision = decisionDe(request);

                  return (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Enfant</h3>
                          <p className="mt-1 text-sm text-gray-900">{kid?.prenom} {kid?.nom}</p>
                        </div>

                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Parent</h3>
                          <p className="mt-1 text-sm text-gray-900">{parent?.prenom} {parent?.nom}</p>
                          <p className="text-sm text-gray-500">{parent?.email}</p>
                          <p className="text-sm text-gray-500">{parent?.telephone}</p>
                        </div>

                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Activité</h3>
                          <p className="mt-1 text-sm text-gray-900">{stage?.title}</p>
                        </div>

                        <div>
                          <h3 className="text-sm font-medium text-gray-500">Dates</h3>
                          <p className="mt-1 text-sm text-gray-900">
                            {session?.start_date && date(session.start_date)} au {session?.end_date && date(session.end_date)}
                          </p>
                        </div>
                      </div>

                      <div className={clsx('rounded-md p-3 text-sm', COULEUR_DU_NIVEAU[decision?.niveau_acces ?? 'aucun'])}>
                        <span className="font-medium">{libelleDuNiveau(decision?.niveau_acces)}</span>
                        {decision?.niveau_acces_le && ` — décidé le ${date(decision.niveau_acces_le)}`}
                        {decision?.niveau_acces_motif && <span className="block">{decision.niveau_acces_motif}</span>}
                      </div>

                      {request.mot_au_parent && (
                        <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                          <span className="font-medium">Mot au parent :</span> {request.mot_au_parent}
                        </div>
                      )}

                      <div className="border-t pt-4">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Détails d'inclusion (au moment de la demande)</h3>

                        {request.inclusion_details?.has_needs && (
                          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                            {[
                              ['Situation', request.inclusion_details.situation_details],
                              ['Impact au quotidien', request.inclusion_details.impact_details],
                              ['Besoin d\'accompagnement', request.inclusion_details.needs_dedicated_staff === 'yes' ? 'Oui, en permanence'
                                : request.inclusion_details.needs_dedicated_staff === 'sometimes' ? 'Par moments'
                                : request.inclusion_details.needs_dedicated_staff === 'no' ? 'Non' : request.inclusion_details.needs_dedicated_staff],
                              ['À quels moments', request.inclusion_details.staff_details],
                              ['Stratégies', request.inclusion_details.strategies],
                              ['Aides techniques', request.inclusion_details.assistive_devices],
                              ['Signes de stress', request.inclusion_details.stress_signals],
                              ['Points forts', request.inclusion_details.strengths],
                              ['Expériences précédentes', request.inclusion_details.previous_experience],
                            ].filter(([, valeur]) => valeur).map(([titre, valeur]) => (
                              <div key={titre as string}>
                                <h4 className="text-sm font-medium text-gray-700">{titre}</h4>
                                <p className="whitespace-pre-line text-sm text-gray-600">{valeur as string}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="border-t pt-4">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">Notes administratives (internes)</h3>
                        <textarea
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-primary-500 focus:border-primary-500"
                          rows={4}
                          placeholder="Ajoutez vos notes ici..."
                        />
                      </div>
                    </>
                  );
                })()}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setIsDetailsModalOpen(false)}
                    className="btn-outline"
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveNotes}
                    disabled={processingRequestId === selectedRequest}
                    className="btn-primary"
                  >
                    {processingRequestId === selectedRequest ? (
                      <span className="flex items-center">
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        Enregistrement...
                      </span>
                    ) : (
                      'Enregistrer les notes'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>

      <Toaster position="top-right" />
    </div>
  );
};

export default AdminInclusionRequestsPage;
